import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from './i18n';
import { parseStructuredMessage } from './utils/helpers';
import { initAnalytics, trackEvent } from './utils/analytics';
import usePdfExport from './hooks/usePdfExport';
import { getUserCity, injectOrigin } from './utils/location';
import AppShell from './components/AppShell';
import ChatThread from './components/ChatThread';
import ChatInput from './components/ChatInput';
import ContactModal from './components/ContactModal';
import FullScreenImageModal from './components/FullScreenImageModal';
import RoteiroView from './components/RoteiroView';
import ExplorationView from './components/ExplorationView';
import BugModal from './components/BugModal';
import DynamicLoading from './components/DynamicLoading';
import './App.css';

function App() {
  const { t } = useTranslation();
  const [chatId, setChatId] = useState(() => {
    const randomId = Math.random().toString(36).substr(2, 9);
    return `user-${randomId}`;
  });
  const [message, setMessage] = useState('');
  const [viewMode, setViewMode] = useState('exploration'); // 'chat', 'roteiro', or 'exploration'
  const [sharedItinerary, setSharedItinerary] = useState(null);
  const [sharedItineraryId, setSharedItineraryId] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('itinerary') || null;
  });
  const [conversation, setConversation] = useState([{
    role: 'assistant',
    content: t('greeting'),
    timestamp: new Date().toISOString(),
    fullResponse: null
  }]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isSharedItineraryLoading, setIsSharedItineraryLoading] = useState(false);
  const [isExplorationLoading, setIsExplorationLoading] = useState(false);
  const [isTyping, setIsTyping] = useState(false);
  const [contactOpen, setContactOpen] = useState(false);
  const [fullScreenImages, setFullScreenImages] = useState([]);
  const [fullScreenImageIndex, setFullScreenImageIndex] = useState(0);
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [bugOpen, setBugOpen] = useState(false);
  const [lastApiPayload, setLastApiPayload] = useState(null);
  const [suggestedAnswers, setSuggestedAnswers] = useState(null);
  const messagesEndRef = useRef(null);
  const chatContainerRef = useRef(null);
  const textareaRef = useRef(null);
  const inputBarRef = useRef(null);

  const handleExportPdf = usePdfExport(conversation);

  const shareContent = async (url, title = 'Itinerary') => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: title,
          url: url
        });
      } else {
        const textArea = document.createElement('textarea');
        textArea.value = url;
        textArea.style.position = 'fixed';
        textArea.style.left = '-999999px';
        textArea.style.top = '-999999px';
        textArea.setAttribute('readonly', '');
        document.body.appendChild(textArea);
        textArea.focus();
        textArea.select();
        textArea.setSelectionRange(0, 99999);
        const result = document.execCommand('copy');
        textArea.remove();
        if (!result) {
          throw new Error('execCommand copy retornou false');
        }
        setCopiedToClipboard(true);
      }
    } catch (err) {
      if (err.name !== 'AbortError') {
        setError(t('error.api', { message: err.message }));
      }
    }
  };

  const handleShareItinerary = async (fullResponse) => {
    try {
      const structuredMessage = parseStructuredMessage(fullResponse);
      if (!structuredMessage) return;

      const response = await fetch(`${process.env.REACT_APP_API_URL}/share-itinerary`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(structuredMessage)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      if (data.url) {
        await shareContent(data.url, t('share.sharedItineraryTitle'));
      }
    } catch (err) {
      setError(t('error.api', { message: err.message }));
    }
  };

  const handleCopyCurrentUrl = async () => {
    try {
      await shareContent(window.location.href, t('share.sharedItineraryTitle'));
    } catch (err) {
      setError(t('error.api', { message: err.message }));
    }
  };

  useEffect(() => {
    if (copiedToClipboard) {
      const timer = setTimeout(() => setCopiedToClipboard(false), 2000);
      return () => clearTimeout(timer);
    }
  }, [copiedToClipboard]);

  const getTopOffset = () => (window.innerWidth >= 1024 ? 16 : 60);

  const scrollToBottom = (smooth = true) => {
    const c = chatContainerRef.current;
    if (!c) return;
    c.scrollTo({ top: c.scrollHeight, behavior: smooth ? 'smooth' : 'auto' });
  };

  const scrollToLastMessageStart = (smooth = true) => {
    const container = chatContainerRef.current;
    if (!container) return false;
    const lastIndex = conversation.length - 1;
    if (lastIndex < 0) return false;
    const el = document.getElementById(`chat-message-${lastIndex}`);
    if (!el) return false;
    let targetTop = el.offsetTop - getTopOffset();
    if (targetTop <= 0) {
      const cRect = container.getBoundingClientRect();
      const eRect = el.getBoundingClientRect();
      targetTop = container.scrollTop + (eRect.top - cRect.top) - getTopOffset();
    }
    container.scrollTo({ top: Math.max(0, targetTop), behavior: smooth ? 'smooth' : 'auto' });
    return true;
  };

  const scrollToTarget = (smooth = true) => {
    const lastMsg = conversation[conversation.length - 1];
    if (lastMsg?.role === 'assistant' && conversation.length > 1) {
      if (!scrollToLastMessageStart(smooth)) scrollToBottom(smooth);
    } else {
      scrollToBottom(smooth);
    }
  };

  useEffect(() => {
    const raf = requestAnimationFrame(() => requestAnimationFrame(() => scrollToTarget(true)));
    const t = setTimeout(() => scrollToTarget(true), 120);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(t);
    };
  }, [conversation]);

  useEffect(() => {
    if (isTyping) {
      const raf = requestAnimationFrame(() => scrollToBottom(true));
      return () => cancelAnimationFrame(raf);
    }
  }, [isTyping]);

  useEffect(() => {
    const lastMessage = conversation[conversation.length - 1];
    if (lastMessage && lastMessage.role === 'assistant' && lastMessage.suggestedAnswers) {
      setSuggestedAnswers(lastMessage.suggestedAnswers);
    } else {
      setSuggestedAnswers(null);
    }
  }, [conversation]);

  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  }, [message]);

  useEffect(() => {
    document.title = `${t('app.name')} - Travel Assistant`;
  }, [t]);

  useEffect(() => {
    initAnalytics();
  }, []);

  useEffect(() => {
    if (sharedItineraryId) {
      const fetchSharedItinerary = async () => {
        setIsSharedItineraryLoading(true);
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/get-itinerary/${sharedItineraryId}`);
          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          const data = await response.json();
          // API returns data in correct format, just clear introduction and finalization
          const transformedItinerary = {
            ...data,
            introduction: '',
            finalization: ''
          };
          setSharedItinerary(transformedItinerary);
          setViewMode('roteiro');
        } catch (err) {
          console.error('Fetch Shared Itinerary Error:', err);
          setError(t('error.api', { message: err.message }));
        } finally {
          setIsSharedItineraryLoading(false);
        }
      };
      fetchSharedItinerary();
    }
  }, [sharedItineraryId, t]);

  useEffect(() => {
    if (viewMode !== 'chat') return;

    const bar = inputBarRef.current;
    const thread = chatContainerRef.current;

    const updatePaddingAndScroll = () => {
      if (bar && thread) {
        const barHeight = bar.offsetHeight || 60;
        const offset = window.innerWidth >= 1024 ? 8 : 40;
        thread.style.paddingBottom = `${barHeight + offset}px`;
      }
      if (thread) {
        if (isTyping) {
          scrollToBottom(true);
        } else {
          const lastMsg = conversation[conversation.length - 1];
          if (lastMsg?.role === 'assistant' && conversation.length > 1) {
            scrollToLastMessageStart(true);
          } else {
            scrollToBottom(true);
          }
        }
      }
    };

    // Run immediately and in the next frame to guarantee DOM has settled
    updatePaddingAndScroll();
    const frameId = requestAnimationFrame(updatePaddingAndScroll);
    const timeoutId = setTimeout(updatePaddingAndScroll, 50);

    let observer = null;
    if (bar && window.ResizeObserver) {
      observer = new ResizeObserver(updatePaddingAndScroll);
      observer.observe(bar);
    }

    return () => {
      cancelAnimationFrame(frameId);
      clearTimeout(timeoutId);
      if (observer) observer.disconnect();
    };
  }, [viewMode, conversation, isTyping]);

  const sendMessageDirectly = async (textToSend, { resetChat = false } = {}) => {
    const trimmed = textToSend?.trim();
    if (!trimmed) return;

    trackEvent('Chat', 'send_message');

    // If resetChat is true, generate a new fresh chatId so backend agent memory starts clean
    let activeChatId = chatId;
    if (resetChat) {
      const randomId = Math.random().toString(36).substr(2, 9);
      activeChatId = `user-${randomId}`;
      setChatId(activeChatId);
    }

    setMessage('');
    setError(null);
    setIsLoading(true);
    setIsTyping(true);
    setViewMode('chat');

    const newUserMessage = {
      role: 'user',
      content: trimmed,
      timestamp: new Date().toISOString(),
      fullResponse: null
    };

    // If resetChat is requested, clear prior conversation and start only with greeting + this message
    if (resetChat) {
      setConversation([
        {
          role: 'assistant',
          content: t('greeting'),
          timestamp: new Date().toISOString(),
          fullResponse: null
        },
        newUserMessage
      ]);
    } else {
      setConversation(prev => [...prev, newUserMessage]);
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/agent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          chatId: activeChatId,
          message: trimmed,
          UserLanguage: navigator.language
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const responseData = await response.json();
      setLastApiPayload({
        request: {
          chatId: activeChatId,
          message: trimmed,
          UserLanguage: navigator.language
        },
        response: responseData
      });
      const structuredMessage = parseStructuredMessage(responseData);

      if (structuredMessage) {
        setConversation(prev => [...prev, {
          role: 'assistant',
          content: structuredMessage.introduction || '',
          timestamp: new Date().toISOString(),
          fullResponse: responseData
        }]);
      } else if (responseData.conversationHistory && Array.isArray(responseData.conversationHistory)) {
        const lastMessage = responseData.conversationHistory[responseData.conversationHistory.length - 1];
        if (lastMessage && lastMessage.role === 'assistant') {
          setConversation(prev => [...prev, {
            ...lastMessage,
            fullResponse: responseData
          }]);
        }
      }
    } catch (err) {
      console.error('API Error:', err);
      setError(t('error.api', { message: err.message }));
    } finally {
      setIsLoading(false);
      setIsTyping(false);
      setIsExplorationLoading(false);
    }
  };

  const handleSend = () => {
    sendMessageDirectly(message);
  };

  const cachedCityRef = useRef(undefined);

  const getPromptWithUserCity = async (prompt) => {
    let city = cachedCityRef.current;
    if (city === undefined) {
      city = await getUserCity();
      if (city) cachedCityRef.current = city;
    }
    return injectOrigin(prompt, city);
  };

  const handleSelectItineraryFromExploration = async (prompt) => {
    if (!prompt || prompt.trim() === '') {
      const randomId = Math.random().toString(36).substr(2, 9);
      setChatId(`user-${randomId}`);
      setViewMode('chat');
      setConversation([{
        role: 'assistant',
        content: t('greeting'),
        timestamp: new Date().toISOString(),
        fullResponse: null
      }]);
      return;
    }
    setIsExplorationLoading(true);
    const finalPrompt = await getPromptWithUserCity(prompt);
    sendMessageDirectly(finalPrompt, { resetChat: true });
  };

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const initialText = params.get('initialtext');
    if (initialText && initialText.trim()) {
      // Clean query parameter from URL without reloading
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
      getPromptWithUserCity(initialText).then(finalPrompt => {
        sendMessageDirectly(finalPrompt);
      });
    }
  }, []);

  const handleHotelSelect = (hotel) => {
    const selectionMessage = `${t('hotel.bookingPrefix')} ${hotel.name || hotel.hotelName || t('hotel.fallbackBookingName')}`;
    setMessage(selectionMessage);
  };

  const handleImageClick = (images, index = 0) => {
    const imageList = Array.isArray(images) ? images : [images];
    setFullScreenImages(imageList);
    setFullScreenImageIndex(index);
  };

  const handleCloseFullScreen = () => {
    setFullScreenImages([]);
    setFullScreenImageIndex(0);
  };

  const handleNextFullScreenImage = () => {
    setFullScreenImageIndex(prevIndex => (prevIndex + 1) % fullScreenImages.length);
  };

  const handlePreviousFullScreenImage = () => {
    setFullScreenImageIndex(prevIndex => (prevIndex - 1 + fullScreenImages.length) % fullScreenImages.length);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleReportBug = () => {
    setBugOpen(true);
  };

  const handleSuggestedAnswerClick = (answer) => {
    setSuggestedAnswers(null);
    sendMessageDirectly(answer);
  };

  const sendBugReport = async (bugMessage) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/contact`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: 'Bug Report',
          email: 'bug-report@system.local',
          message: bugMessage
        })
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (err) {
      console.error('Bug Report Error:', err);
      throw err;
    }
  };

  return (
    <div className="app">
      {isSharedItineraryLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p className="loading-text">{t('share.loadingItinerary')}</p>
        </div>
      )}
      {isExplorationLoading && <DynamicLoading />}
      <FullScreenImageModal
        images={fullScreenImages}
        currentIndex={fullScreenImageIndex}
        onClose={handleCloseFullScreen}
        onNext={handleNextFullScreenImage}
        onPrevious={handlePreviousFullScreenImage}
      />
      <ContactModal isOpen={contactOpen} onClose={() => setContactOpen(false)} />
      <BugModal isOpen={bugOpen} onClose={() => setBugOpen(false)} lastApiPayload={lastApiPayload} onSendMessage={sendBugReport} />
      <AppShell
        onContactClick={() => setContactOpen(true)}
        viewMode={viewMode}
        onViewModeChange={setViewMode}
        showRoteiroButton={Boolean(sharedItinerary)}
        chatInput={viewMode === 'chat' ? (
          <ChatInput
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onSend={handleSend}
            isLoading={isLoading}
            inputBarRef={inputBarRef}
            textareaRef={textareaRef}
            onKeyDown={handleKeyDown}
            suggestedAnswers={suggestedAnswers}
            onSuggestedAnswerClick={handleSuggestedAnswerClick}
          />
        ) : null}
      >
        <div className="dev-fields">
          <input
            type="text"
            value={chatId}
            onChange={(e) => setChatId(e.target.value)}
            placeholder={t('chat.devChatId')}
          />
        </div>
        {viewMode === 'roteiro' && sharedItinerary ? (
          <RoteiroView
            itinerary={sharedItinerary}
            onShareItinerary={handleShareItinerary}
            onCopyCurrentUrl={handleCopyCurrentUrl}
            copiedToClipboard={copiedToClipboard}
            setCopiedToClipboard={setCopiedToClipboard}
          />
        ) : viewMode === 'exploration' ? (
          <ExplorationView onSelectItinerary={handleSelectItineraryFromExploration} />
        ) : (
          <ChatThread
            chatContainerRef={chatContainerRef}
            messagesEndRef={messagesEndRef}
            conversation={conversation}
            isTyping={isTyping}
            error={error}
            onHotelSelect={handleHotelSelect}
            onImageClick={handleImageClick}
            onExportPdf={handleExportPdf}
            onShareItinerary={handleShareItinerary}
            onReportBug={handleReportBug}
            copiedToClipboard={copiedToClipboard}
            setCopiedToClipboard={setCopiedToClipboard}
          />
        )}
      </AppShell>
    </div>
  );
}

export default App;
